// A site's devices as two lists, access points and switches, each with the
// columns that mean something for that kind of device (#878). The words each
// cell carries are decided in `family.ts`; this reads what those functions need
// once for the page and places their answers.
//
// **Reads, once for the page and never per row** (ZENITH.md §9.5):
//   - each switch's live port state, every switch with at most the server's
//     `state_fan_out` in flight at once — the topology's reads under the
//     topology's bound (ADR-0045 decision 5, #1786), made here by the same
//     `SwitchRead` — for the ports, the PoE and both uplinks;
//   - the site's work list (ADR-0117), only for a caller who holds
//     `network.write`, which is what the read requires;
//   - the site's plan and its locations, Zenith's store, for where a device is.
// An access point is never asked (ADR-0045 decision 6). Each row's address is
// not a read of this file's: the page reads the list with `read_lan_address`
// automatically as it loads (ADR-0443 amending ADR-0391), and hands it here.
import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { equipmentLabel, type EquipmentLabel } from "./pieces";
import { api, unattended } from "../../api/client";
import { shared } from "../../api/writes";
import type { components } from "../../api/schema";
import { SKIP, UNATTENDED, useQuery } from "../../api/hooks";
import { heldGenerated, reasonOf, type useCarried } from "../../carried";
import { EntityRef, IdRef } from "../../shell/EntityRef";
import type { Entity } from "../../shell/entity";
import { ObjectLink } from "../../shell/ObjectLink";
import { ScopeLink } from "../../shell/scope";
import { headCells, SkeletonRows, When } from "../../ui";
import type { Device, FirmwareDelivery } from "./commands";
import { DeliveryMarker, markerOf } from "./delivery";
import DeviceActions, { type ActionHandlers } from "./DeviceActions";
import { apConfigurationOf, ClientsCell, ConfigurationCell, CONVERGENCE, State, STATUS_RANK, StatusState, pushOf, runningOf, statusOf, switchConfigurationOf, type StateWords, type useClients, type useConvergence } from "./DevicesTable";
import { apUplinkFrom, designOf, locationOf, poeConsumed, poeOf, portsOf, portsUpCount, switchUplinkOf, uplinkIndex, WORK_LIST_PATH, type Target, type WorkListRead } from "./family";
import { firstBy } from "./firstBy";
import { SortHeader, sortRows, type Sort, type SortValue } from "./narrow";
import { namer, useLinks } from "./Links";
import { SwitchRead, observationOf, type Read } from "./switchRead";
import { useFanOut } from "./fanOut";
import { readOrder, uplinksOf, type Observation } from "./verdicts";
import { AscendingIcon, Glyph, OpenIcon } from "../../icons";
import { cdsListChipOf } from "./CdsRecord";

/** The site's switches read as the topology reads them: one `ports/state` per
 * switch, every switch, with at most the server's `state_fan_out` in flight at
 * once (#1786). The figure comes with the site's links, so nothing is asked
 * before they answer. `readers` are the per-switch read components, which the
 * page renders once; `reload` reads them all again. */
export function useSwitchObservations(siteId: string | undefined, switches: Device[]) {
  const links = useLinks(siteId);
  const bound = links.data?.state_fan_out;
  const fanOut = useFanOut(bound);
  const [tick, setTick] = useState(0);
  const [reads, setReads] = useState<Record<string, Read>>({});
  const read = useMemo(() => bound ? readOrder(switches) : [], [switches, bound]);
  const onRead = useCallback((serial: string, next: Read) => setReads((r) => {
    const have = r[serial];
    return have && have.status === next.status && have.data === next.data && have.problem === next.problem ? r : { ...r, [serial]: next };
  }), []);
  const linksFailed = links.status === "error";
  const observed = useMemo(() => {
    const m = new Map<string, Observation>();
    if (bound) for (const d of read) m.set(d.serial, observationOf(reads[d.serial]));
    else for (const d of switches) m.set(d.serial, { kind: "unread", why: linksFailed ? "not read: the site's links, which carry the read bound, could not be read" : "the switch is still being read" });
    return m;
  }, [read, switches, reads, bound, linksFailed]);
  const readers = read.map((d) => <SwitchRead key={d.serial} serial={d.serial} tick={tick} fanOut={fanOut} onRead={onRead} />);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { observed, readers, reload };
}

/** The site's work list, read only for a caller who may read it: the read is
 * `network.write` (ADR-0117), and asking without it would be a refusal the
 * page then has to explain as something other than what it is.
 *
 * **One read for every asker at once** (#1811). The read plans each managed
 * switch at the cell, and the shell's Changes to apply asks for it on every
 * page of the site, so a page that asks while the shell's read is out joins
 * it rather than sending a second (`shared`, `api/writes.ts`), unless a write
 * landed since that read started. `unattended` marks every read this hook
 * makes as asked by nobody, which is what the shell's are. */
export function useWorkListRead(siteId: string | undefined, may: boolean, opts: { unattended?: boolean } = {}): WorkListRead & { reload(mark?: unknown): void } {
  const all = !!opts.unattended;
  const asked = useRef(false);
  const q = useQuery((how) => {
    // A person's own reload is use even where every other read is not.
    const mark = { unattended: all ? !asked.current : how.unattended };
    asked.current = false;
    return siteId && may
      ? shared(`work-list:${siteId}`, () => api.GET("/sites/{id}/work-list", { params: { path: { id: siteId }, header: unattended(mark) } }))
      : SKIP;
  }, [siteId, may, all]);
  const { reload: again } = q;
  const reload = useCallback((m?: unknown) => { asked.current = m !== UNATTENDED; again(m); }, [again]);
  if (!may) return { kind: "forbidden", reload };
  if (q.status === "error") return { kind: "error", problem: q.problem, reload };
  if (q.status === "ready") return { kind: "ready", list: q.data, reload };
  return { kind: "loading", reload };
}

/** The site's locations, for the name a bound planned device's location
 * carries. Zenith's store, one read, the whole set. */
export function useLocations(siteId: string | undefined) {
  return useQuery(() => siteId ? api.GET("/sites/{id}/planner/locations", { params: { path: { id: siteId } } }) : SKIP, [siteId]);
}

/** One labelled line of a cell: a word, or a dash while its read is out. */
function Line({ label, words, to }: { label: string; words?: StateWords; to?: string }) {
  return (
    <div className="doc-line">
      <span className="doc-line-label">{label}</span>{" "}
      {words
        ? to ? <ScopeLink to={to} title={words.title}><State words={words} /></ScopeLink> : <State words={words} />
        : <span className="muted">—</span>}
    </div>
  );
}

/** A plain cell for a decided word: quiet type, the pill when it needs
 * attention, and a dash while its read is out. */
function Words({ words }: { words?: StateWords }) {
  return words ? <State words={words} /> : <span className="muted">—</span>;
}

// --- What every row shares, and which columns are drawn (UX-008) -----------
//
// The 2026-11-01 review measured 30 access points at about 1,750 px: every row
// carried a three-line Configuration cell saying the same three words as every
// other row, and Location and Model said "not in the plan" and "not recognized"
// thirty times. **A word every row shares is said once, above the table, and
// the cell or the column that would repeat it is not drawn.** Each fact keeps
// its own label in that line — Device, Design and Source stay three facts and
// are never merged into one word (ADR-0099, ADR-0117; `DOCUMENT_TITLE`) — and
// keeps its pill where it is marked, so a word that needs attention still
// looks it. The line is over the rows drawn, so a filter that leaves one
// differing row draws the column again.

/** A shared word, with the label it is said under and the page it links to. */
type Same = StateWords & { label: string; to?: string };
/** The facts every drawn row shares, by key: `device`, `design` and `source`
 * are the Configuration column's three lines, and `location`, `push` and
 * `firmware` are columns. */
export type Uniform = Map<string, Same>;
const NONE_SHARED: Uniform = new Map();

/** What `PushCell` draws as a dash, as a word the shared line can say. */
const NOTHING_PUSHED: StateWords = { word: "nothing written from here" };

interface Fact { key: string; label: string; of(d: Device): StateWords | undefined; to?(w: StateWords): string | undefined }

/** The facts whose word and tone are the same on every row, and only where
 * there are two rows or more: one row shares nothing with anything. A row whose
 * read is still out has no word, so nothing is shared until every row has one.
 * The hover text is kept only where it is the same on every row too: a
 * generation number in one row's is not every row's. */
export function uniformOf(rows: Device[], facts: Fact[]): Uniform {
  if (rows.length < 2) return NONE_SHARED;
  const out: Uniform = new Map();
  for (const f of facts) {
    const first = f.of(rows[0]!);
    if (!first) continue;
    let title = first.title;
    const every = rows.every((d) => {
      const w = f.of(d);
      if (!w || w.word !== first.word || w.tone !== first.tone) return false;
      if (w.title !== title) title = undefined;
      return true;
    });
    if (every) out.set(f.key, { word: first.word, tone: first.tone, title, label: f.label, to: f.to?.(first) });
  }
  return out;
}

/** The line above the table that says, once, what every row shares. */
function SharedLine({ same, noun, n }: { same: Uniform; noun: string; n: number }) {
  if (same.size === 0) return null;
  return (
    <p className="table-same">
      <span className="table-same-lead">On all {n} {noun} shown:</span>
      {[...same.values()].map((w) => (
        <span key={w.label} className="table-same-fact">
          <span className="doc-line-label">{w.label}</span> {w.to ? <ScopeLink to={w.to}><State words={w} /></ScopeLink> : <State words={w} />}
        </span>
      ))}
    </p>
  );
}

/** The columns a reader may hide, with the header each carries. Name, the
 * CDS column and the row's actions are always drawn. */
export const AP_COLUMNS = [["model", "Model"], ["address", "Address"], ["location", "Location"], ["uplink", "Uplink"], ["document", "Configuration"], ["push", "Last push"], ["firmware", "Firmware"], ["clients", "Clients"], ["contact", "Last contact"]] as const;
export const SWITCH_COLUMNS = [["model", "Model"], ["address", "Address"], ["ports", "Ports up"], ["poe", "PoE"], ["uplink", "Uplink"], ["document", "Configuration"], ["push", "Last push"], ["firmware", "Firmware"], ["contact", "Last contact"]] as const;
export type Family = "ap" | "switch";

const columnsKey = (family: Family) => `zenith.devices.hidden-columns.${family}`;
function readHidden(family: Family): Set<string> {
  try {
    const raw = localStorage.getItem(columnsKey(family));
    const v: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);
  } catch { return new Set(); }
}

/** The columns this browser hides on a family's table, kept in `localStorage`
 * like the theme: a choice about this screen, not about the site, so it is
 * the same at every site and is not in the address. */
export function useHiddenColumns(family: Family): [ReadonlySet<string>, (next: Set<string>) => void] {
  const [held, setHeld] = useState(() => ({ family, hidden: readHidden(family) }));
  // Read again the moment the family changes, as the page's own view is (`fresh`), and held from the next choice.
  const hidden = held.family === family ? held.hidden : readHidden(family);
  const set = useCallback((next: Set<string>) => {
    try { localStorage.setItem(columnsKey(family), JSON.stringify([...next])); } catch { /* private mode: kept for this page only */ }
    setHeld({ family, hidden: next });
  }, [family]);
  return [hidden, set];
}

/** The column chooser, a disclosure in the list's toolbar. */
export function ColumnChooser({ family, hidden, onChange }: { family: Family; hidden: ReadonlySet<string>; onChange(next: Set<string>): void }) {
  const columns = family === "switch" ? SWITCH_COLUMNS : AP_COLUMNS;
  const off = columns.filter(([k]) => hidden.has(k)).length;
  return (
    <details className="column-chooser">
      <summary className="btn btn-quiet">Columns{off > 0 ? ` (${off} hidden)` : ""}</summary>
      <fieldset className="column-chooser-list">
        <legend className="sr-only">Columns shown</legend>
        {columns.map(([k, label]) => (
          <label key={k} className="account-check">
            <input type="checkbox" checked={!hidden.has(k)} onChange={() => { const next = new Set(hidden); if (!next.delete(k)) next.add(k); onChange(next); }} />
            {label}
          </label>
        ))}
        {off > 0 && <button type="button" className="btn btn-sm btn-quiet" onClick={() => onChange(new Set())}>Show every column</button>}
      </fieldset>
    </details>
  );
}

/** A long value on one line, cut to the column's width (UX-008): a firmware
 * string wrapped over three lines and set every row's height. The whole value
 * is the button's own text, so a screen reader reads all of it, and pressing
 * it — by click, tap or key — shows it whole; it is not a hover-only title. */
function Clip({ value, max = 18 }: { value: string; max?: number }) {
  const [open, setOpen] = useState(false);
  if (value.length <= max) return <>{value}</>;
  return (
    <button type="button" className="cell-clip" aria-expanded={open} style={{ maxWidth: open ? undefined : `${max}ch` }} onClick={() => setOpen((o) => !o)}>
      {value}
    </button>
  );
}

/** The document state: what the device runs against what its controller
 * holds, and whether Design holds changes the last apply did not carry. Two
 * facts on two lines, each from its own read, and never merged into one word,
 * and under them where the document comes from. */
function DocumentCell({ device, target, convergence, work, carried, same }: {
  device: Device; target: Target;
  convergence: ReturnType<typeof useConvergence>;
  work: WorkListRead;
  carried?: ReturnType<typeof useCarried>;
  /** The lines every row shares, said once above the table (UX-008) and not repeated here. */
  same: Uniform;
}) {
  const design = designOf(target, work);
  return (
    <>
      {!same.has("device") && <Line label="Device" words={runningOf(device.serial, convergence)} />}
      {!same.has("design") && <Line label="Design" words={design} to={work.kind === "ready" && design?.tone ? WORK_LIST_PATH : undefined} />}
      {/* Where the document comes from, which the single list's Configuration
          column said (ADR-0019 decision 4; ADR-0074 decision 3): an access
          point's source from carry-over readiness, a switch's whether Zenith
          manages its ports. It is why the Design line can name the device at
          all, and it is a third fact, not a qualifier of the other two. */}
      {same.has("source") ? null : device.category === "switch"
        ? <Line label="Ports" words={switchConfigurationOf(device)} />
        : carried && <div className="doc-line"><span className="doc-line-label">Source</span> <ConfigurationCell device={device} carried={carried} /></div>}
    </>
  );
}

function PushCell({ device, convergence }: { device: Device; convergence: ReturnType<typeof useConvergence> }) {
  // ADR-0485: the convergence read says whether the outcome still describes the device.
  const w = pushOf(device, convergence);
  if (!w) return <span className="muted" title="Nothing has been written to this device from here.">—</span>;
  return <State words={w} />;
}

/** The row's identity: its name, linked to its page, and its status under it,
 * in the frozen first cell (#830). */
/** The site's firmware deliveries not yet final, by serial (ADR-0403 `2b`), or
 * "unread" when the read failed — which draws no marker either way and says so
 * under the table, never as *no upgrade under way*. */
export type DeliveriesRead = Map<string, FirmwareDelivery> | "unread";

function Identity({ device, label, delivery }: { device: Device; label: EquipmentLabel; delivery?: FirmwareDelivery }) {
  const entity: Entity = { kind: "device", serial: device.serial, name: device.name, mac: device.mac, switch: device.category === "switch", siteId: device.site_id, siteName: device.site_name };
  return (
    <th scope="row">
      {/* The friendly name, and the serial under it once (#1906, `pieces.tsx`). */}
      {/* The name and its menu (#2344): copy, scope to its site, narrow, open. */}
      <EntityRef entity={entity}>
        <ObjectLink kind="device" value={device.serial}>{label.label}</ObjectLink>
      </EntityRef>
      {/* Restates ADR-0403 decision 3a as amended 2026-10-17 (#1841) — the row's marker says an upgrade
          is under way or its outcome is not known, and links to the device's page, where the Firmware
          card is: the row itself offers no Upgrade. */}
      <div className="device-sub"><StatusState device={device} />{delivery && markerOf(delivery.state) && <> <ObjectLink kind="device" value={device.serial}><DeliveryMarker delivery={delivery} /></ObjectLink></>}</div>
      {/* The serial under it copies whole and opens the same menu, named for the serial (#2367). */}
      {label.serial && <div className="muted small"><IdRef entity={entity} value={label.serial} copy="Serial" /></div>}
    </th>
  );
}

/** Under a device's hardware string, the catalogue model its row resolved to
 * (#355, #1120): a link to its card, or "not recognised" with the reason on
 * hover and the candidates linked when there are any, each to its card with
 * the others named (#375). The list carries the model for every device from
 * the row it already reads — the string it declares, else a switch's ports —
 * so this costs no call; a switch whose row could not be read carries none.
 *
 * Restates ADR-0193 decision 4a: where the string wins over ports that match
 * another model, that model is said beside it rather than dropped. */
function ModelLine({ device }: { device: Device }) {
  const m = device.catalogue_model;
  if (!m) return null;
  if (m.state === "recognised" && m.model) {
    return (
      // Restates ADR-0307 decision 4a: a model keyed on the string the device's capabilities record
      // declared, where the cell's record holds none, says where the string came from.
      <div className="muted" title={m.unrecorded_string ? `It declares ${m.unrecorded_string}, which no catalog model records yet, so its ports decided.` : m.declared_from === "capabilities" ? CAPABILITIES_DECLARED : undefined}>
        <ScopeLink to="/device-models" hash={m.model} title="Open this model in the catalog">{m.model}</ScopeLink>
        {m.ports_model && <> · ports match <ScopeLink to="/device-models" hash={m.ports_model} title="Open this model in the catalog">{m.ports_model}</ScopeLink></>}
      </div>
    );
  }
  const candidates = m.candidates ?? [];
  return (
    <div className="muted" title={m.reason}>
      not recognized
      {candidates.length > 0 && <> · one of {candidates.map((c, i) => <span key={c}>{i > 0 && ", "}<ScopeLink to="/device-models" hash={[c, ...candidates.filter((o) => o !== c)].join(",")} title="Open this model in the catalog">{c}</ScopeLink></span>)}</>}
    </div>
  );
}

/** The model as the gateway carries it, and the catalogue model the list
 * resolved it to.
 *
 * **The column is a fact about the cell** (ADR-0208 decision 2): it renders
 * what the site's cell reports the device as, and nothing Zenith holds from an
 * import stands in for it — an export's model is a vendor's claim of unknown
 * truth, measured false for 75 devices at Saugatuck (#1097). So a carried
 * access point the cell has no record of renders a dash, and what the row owes
 * the operator is *why* (decision 3): the same fact the Source line under
 * Document already says, `carried · not on the controller yet`, said again where the
 * dash is read. The reason comes from carry-over readiness, which the page
 * already reads for that line — no read is added. A switch passes no
 * `carried`: its Source is its ports, and readiness does not report one. */
function ModelCell({ device, carried }: { device: Device; carried?: ReturnType<typeof useCarried> }) {
  if (device.manufacturer) return <>{device.manufacturer}<ModelLine device={device} /></>;
  const row = carried?.data && firstBy(carried.data.devices, "serial").get(device.serial);
  const reason = row?.reason;
  if (!row || (reason !== "not_on_cell" && reason !== "held_on_cell")) {
    return <><span className="muted" title="The controller's record of this device carries no manufacturer.">—</span><ModelLine device={device} /></>;
  }
  // A record an operator's generated apply wrote has no import behind it, so its line names none (#1715).
  const why = reason === "not_on_cell" ? MODEL_NOT_ON_CELL : heldGenerated(row) ? MODEL_HELD_GENERATED : MODEL_HELD_ON_CELL;
  const entry = reasonOf(row)!;
  return (
    <>
      <span className="muted">—</span>
      {/* Restates ADR-0208 decisions 2 and 3 — the Model column is what the cell reports, a carried
          access point the cell holds no record of has reported nothing, and the import's model is
          not shown in its place. Restates ADR-0227 decision 3a — a record the seed wrote carries no
          manufacturer either, and owgw never fills one on an existing record, so the dash stands for
          the life of such a record and this line says why rather than showing a dash with no reason. */}
      <div className="muted" title={`${why} ${entry.next}`}>{entry.label}</div>
      <ModelLine device={device} />
    </>
  );
}

// Restates ADR-0307 decision 4a — the catalogue line's source where the cell's record holds no model
// string: the device's own capabilities record, as Zenith's copy of the listing learned it.
const CAPABILITIES_DECLARED = "Declared by the device's capabilities record when it connected. The controller's record of this device holds no model string, so the column above is a dash.";

// Restates ADR-0208 decision 2 — see ModelCell: the column is the cell's report and the export's
// model is not rendered in its place.
const MODEL_NOT_ON_CELL = "This column is what the site's controller reports the device as, and the controller has no record of this access point yet, so there is nothing to show. The model its import names is the export's claim, not the controller's, and is not shown here.";

// Restates ADR-0227 decision 3a — the record the seed wrote leaves manufacturer empty, because the
// only truthful value is the device's own declared string and Zenith does not have it before the
// device connects, and the cell never fills it on an existing record, so this column's dash stands
// for as long as the record does. Restates ADR-0307 decisions 1a, 2c and 4a — once the device has
// connected, the model its own capabilities record declares is learned off-request by Zenith's copy
// of the listing, and the catalogue line under the dash resolves from that string and says so;
// the column itself keeps showing the record's empty field.
const MODEL_HELD_RECORD = "This column is what the site's controller reports the device as. The controller holds a record for this access point that was written from here before the device arrived, and that record carries no manufacturer: the only truthful value is the device's own, and the controller does not fill it in later, so this stays a dash. Once the device has connected and declared its model in its capabilities record, the catalog line below resolves from that and says so.";
const MODEL_HELD_ON_CELL = `${MODEL_HELD_RECORD} The model its import names is the export's claim, not the controller's, and is not shown here.`;

// Restates ADR-0276 decisions 1b and 4a with ADR-0227 decision 3a — an operator's generated apply
// writes the record through the seed's own transport, so it carries no manufacturer either, and
// there is no import behind it to name a model. The wording is proposed on #1715's merge request.
const MODEL_HELD_GENERATED = MODEL_HELD_RECORD;

// Restates ADR-0208 decision 2, ADR-0193 — the access points' Model header: the string is the
// cell's report, and the catalogue line under it is resolved from that string alone.
const AP_MODEL_TITLE = "What this site's controller reports the access point as, and under it the catalog model that string resolves to. An access point the controller has no record of yet has reported nothing, and says so.";

// Restates ADR-0099, ADR-0117 — the Configuration column's header, below: the Device line is what
// the controller's status record reports the device running against what the controller
// holds, which is the device's own answer only while no push is outstanding (ADR-0099); the
// Design line is whether the site's work list names an apply for the device's target, which
// exists exactly when that target's plan would change something (ADR-0117 decision 1). Two
// facts from two reads, never one word.
const DOCUMENT_TITLE = "Two facts, never merged. Device: which generation the controller reports this device running, against the one it holds — the device's own answer only while no push is outstanding. Design: whether the site's work list names a plan to apply for this device — the venue's for an access point, the switch's own for a switch — which it does exactly when that plan would change something.";

// Restates ZENITH.md §10.2 — what Zenith sent and what the device runs are two columns and
// never one: Last push beside Document, as the single list drew them (#835 is unruled).
const PUSH_TITLE = "Where the last write made to this device from here got to. Not what the device is running — that is the Device line of Configuration, beside it.";

/** The columns each table sorts on (#1259, #834): every column where an order
 * means something. The actions column has none. */
export type ApSortKey = "name" | "status" | "model" | "location" | "uplink" | "document" | "push" | "firmware" | "clients" | "contact";
export type SwitchSortKey = "name" | "status" | "model" | "ports" | "poe" | "uplink" | "document" | "push" | "firmware" | "contact";


/** Document in `CONVERGENCE`'s order, current first, with "changed outside" —
 * `convergenceWord`'s one word that is not a state's (#1383) — after the
 * `unknown` it is a reading of. A device the read has no row for has no value,
 * and sorts last. */
const DOCUMENT_RANK = new Map<string, number>([...Object.values(CONVERGENCE).map((c) => c.label), "changed outside", "not judged"].map((w, i) => [w, i]));

/** A word's sort value, with the words that say a read did not answer read as
 * absences: "not read" is not a location or an uplink, and sorting it among
 * them would interleave rows that say nothing with rows that answer. */
const UNANSWERED = new Set(["not read", "not reported"]);
const wordOf = (w: StateWords | undefined): SortValue => w && !UNANSWERED.has(w.word) ? w.word : undefined;
const contactOf = (d: Device): SortValue => d.last_contact_at ? Date.parse(d.last_contact_at) : undefined;

/** The frozen first column's head. Status has no column of its own — it is
 * under the name, in the identity cell (#830) — so the head carries both
 * orders: Name, and Status beside it. `aria-sort` is on the `th` and says
 * whichever of the two is in force. */
function IdentityHead<K extends string>({ sort, onSort }: { sort?: Sort<K>; onSort?(key: K): void }) {
  if (!onSort) return <th scope="col">Name</th>;
  const on = sort?.key === "name" || sort?.key === "status" ? sort : undefined;
  const button = (k: K, label: string) => (
    <button type="button" className="th-sort" onClick={() => onSort(k)}>
      {label}{on?.key === k && <Glyph icon={on.dir === "asc" ? AscendingIcon : OpenIcon} size={12} className="sort-mark" />}
    </button>
  );
  return (
    <th scope="col" aria-sort={on ? (on.dir === "asc" ? "ascending" : "descending") : "none"}>
      {button("name" as K, "Name")} {button("status" as K, "Status")}
    </th>
  );
}

type Shared = ActionHandlers & {
  /** The family's whole list, as read. Names and uplinks are resolved against
   * it, so a row narrowed out of view still names its neighbours. */
  items: Device[];
  convergence: ReturnType<typeof useConvergence>;
  work: WorkListRead;
  busy: boolean;
  mayCommand: boolean; mayConsole: boolean;
  /** The address read, made automatically for the open family's online rows
   * (ADR-0443 amending ADR-0391, #1901). While it is in flight it is
   * `loading`; absent, no read was made for this table, and the dash carries
   * `UNASKED`'s title. */
  addresses?: AddressRead;
  /** The CDS column and the row selection the site's device list's bulk ask acts on (ADR-0522 decision
   * 6, #2212), a superuser's alone (decision 5); absent, neither is drawn. */
  cds?: CdsColumn;
  /** The columns the reader chose to hide (`useHiddenColumns`). */
  hidden?: ReadonlySet<string>;
};

/** Whether a column is drawn: not hidden by the reader, and not one whose
 * word every row shares, which the line above the table says instead. The
 * Configuration column goes only when all three of its lines are shared. */
type Visible = (key: string) => boolean;
const ALL: Visible = () => true;
function visibleOf(hidden: ReadonlySet<string> | undefined, same: Uniform): Visible {
  return (k) => !hidden?.has(k) && !(k === "document" ? same.has("device") && same.has("design") && same.has("source") : same.has(k));
}

/** What the CDS column needs from the page: which rows are selected, and how a click changes that. */
export interface CdsColumn {
  selected: ReadonlySet<string>;
  onToggle(serial: string): void;
  /** Select, or clear, every row the table draws: the filtered set, when a filter is on. */
  onToggleAll(serials: string[], on: boolean): void;
}

/** The CDS column's head: a box selecting every row drawn, and the column's name. */
function CdsHead({ cds, drawn }: { cds?: CdsColumn; drawn: Device[] }) {
  if (!cds) return <th scope="col">CDS</th>;
  const all = drawn.length > 0 && drawn.every((d) => cds.selected.has(d.serial));
  return (
    <th scope="col">
      <input type="checkbox" aria-label="Select every device shown" checked={all} style={{ marginRight: 4 }}
        onChange={() => cds.onToggleAll(drawn.map((d) => d.serial), !all)} />CDS
    </th>
  );
}

/** A row's CDS cell (ADR-0522 decision 6): its selection box and the device's CDS chip, which is empty
 * for a record that points here or was transferred (`cdsListChipOf`), so a healthy site's column is
 * empty. The reading and the request are the list's own fields; nothing here reads CDS. */
function CdsCell({ device, cds }: { device: Device; cds: CdsColumn }) {
  const chip = cdsListChipOf(device);
  return (
    <td className="cds-cell">
      <input type="checkbox" aria-label={`Select ${device.name}`} checked={cds.selected.has(device.serial)}
        onChange={() => cds.onToggle(device.serial)} />
      {chip && <> <span className={`pill pill-${chip.tone}`}><span className="dot" aria-hidden="true" />{chip.label}</span></>}
    </td>
  );
}

function Actions({ device, p }: { device: Device; p: Shared }) {
  return (
    <td className="row-actions">
      {/* The device acts alone (#875, ADR-0149 decision 2): what changes a
          device's record or the plan is Design › Physical › Equipment's. */}
      <DeviceActions as="menu" device={device} busy={p.busy} mayCommand={p.mayCommand} mayConsole={p.mayConsole} mayPoeCycle={p.mayPoeCycle} onAct={p.onAct} onConsole={p.onConsole} />
    </td>
  );
}

/** The table's own drawing convention (#830), carried over from the single
 * list. **This restates no ruled decision**: it says how the table marks. It
 * was a note under the table, and is the last sentence of the Devices page's ⓘ
 * beside its title since #1900. */
export const FAMILY_MARKS = "Status shows a dot for every state. In the other columns, a dot and a color mark only a state that needs attention. A word in plain type is the normal state.";

/** A table and what the reads under it came to, said once under it. */
function Frame({ label, head, rows, note, busy, above }: { label: string; head: ReactNode; rows: ReactNode; note?: ReactNode;
  /** Said once above the table: what every row shares (UX-008). */
  above?: ReactNode;
  /** The table before its rows arrive (#1319): busy, its body hidden from
   * assistive technology, `rows` being the placeholders. */
  busy?: boolean }) {
  return (
    <>
      {above}
      <div className="table-wrap"><table className="freeze-first devices-table" aria-label={label} aria-busy={busy || undefined}>
        <thead><tr>{head}</tr></thead>
        <tbody aria-hidden={busy || undefined}>{rows}</tbody>
      </table></div>
      {note}
    </>
  );
}

/** The access points' header row, built here for the table and for its
 * skeleton (#1319) so the two cannot differ. Sortable where `onSort` is given,
 * plain `<th>`s where it is not (the customer's list). */
function apHead(sort?: Sort<ApSortKey>, onSort?: (key: ApSortKey) => void, cdsHead?: ReactNode, vis: Visible = ALL) {
  const h = (k: ApSortKey, label: string, opts: { title?: string; className?: string } = {}) => onSort
    ? <SortHeader k={k} sort={sort} onSort={onSort} {...opts}>{label}</SortHeader>
    : <th scope="col" {...opts}>{label}</th>;
  return (
    <>
      <IdentityHead sort={sort} onSort={onSort} />
      {vis("model") && h("model", "Model", { title: AP_MODEL_TITLE })}
      {vis("address") && <th scope="col" title={ADDRESS_TITLE}>Address</th>}
      {vis("location") && h("location", "Location", { title: "The location of the planned device bound to this serial, from the site's plan." })}
      {vis("uplink") && h("uplink", "Uplink", { title: "The switch and port that report this access point as their LLDP neighbor, as each switch reports it now. Observation only; declared cabling is on the topology." })}
      {vis("document") && h("document", "Configuration", { title: DOCUMENT_TITLE })}
      {vis("push") && h("push", "Last push", { title: PUSH_TITLE })}
      {vis("firmware") && h("firmware", "Firmware")}
      {vis("clients") && h("clients", "Clients", { className: "num" })}
      {vis("contact") && h("contact", "Last contact")}
      {cdsHead}
      <th scope="col"><span className="sr-only">Actions</span></th>
    </>
  );
}

/** The switches' header row, shared the same way (#1319). */
function switchHead(sort?: Sort<SwitchSortKey>, onSort?: (key: SwitchSortKey) => void, cdsHead?: ReactNode, vis: Visible = ALL) {
  const h = (k: SwitchSortKey, label: string, title?: string) => onSort
    ? <SortHeader k={k} sort={sort} onSort={onSort} title={title}>{label}</SortHeader>
    : <th scope="col" title={title}>{label}</th>;
  return (
    <>
      <IdentityHead sort={sort} onSort={onSort} />
      {vis("model") && h("model", "Model", "What the switch reports itself as, and under it the catalog model its own declared ports resolve to.")}
      {vis("address") && <th scope="col" title={ADDRESS_TITLE}>Address</th>}
      {vis("ports") && h("ports", "Ports up", "Ports with a carrier, of the ports the switch's own configuration declares, from its last state report.")}
      {vis("poe") && h("poe", "PoE", "What the switch reports it delivers over PoE for the whole unit, against its budget.")}
      {/* Restates ADR-0088 and ADR-0448 — the uplink is the switch's own mark; the switch beyond it is the one its report names there, or the one whose forwarding table learned it. */}
      {vis("uplink") && h("uplink", "Uplink", "The port this switch reaches its controller through, and what is beyond it: what its report names there, or the switch whose forwarding table learned it.")}
      {vis("document") && h("document", "Configuration", DOCUMENT_TITLE)}
      {vis("push") && h("push", "Last push", PUSH_TITLE)}
      {vis("firmware") && h("firmware", "Firmware")}
      {vis("contact") && h("contact", "Last contact")}
      {cdsHead}
      <th scope="col"><span className="sr-only">Actions</span></th>
    </>
  );
}

export function AccessPointsTable({ switches, observed, planned, locations, clients, carried, deliveries, rows, sort, onSort, ...p }: Shared & {
  switches: Device[];
  /** Which access points a firmware delivery is under way for (ADR-0403 `1a`, `2b`). Absent is
   * not asked, which draws no marker. */
  deliveries?: DeliveriesRead;
  observed: Map<string, Observation>;
  /** The site's plan and its locations, or "unread" when a read failed. */
  planned?: components["schemas"]["PlannedDevice"][] | "unread";
  locations?: components["schemas"]["Location"][] | "unread";
  clients: ReturnType<typeof useClients>;
  carried: ReturnType<typeof useCarried>;
  /** The rows to draw — `items` narrowed — in the API's order. */
  rows?: Device[];
  sort?: Sort<ApSortKey>;
  onSort?(key: ApSortKey): void;
}) {
  const plan = useMemo(() => planned instanceof Array ? planned : [], [planned]);
  const name = useMemo(() => namer([...switches, ...p.items], plan), [switches, p.items, plan]);
  const labelOf = useMemo(() => labelsOf(plan), [plan]);
  // Every read switch's LLDP neighbours, once per observation answer (#1309):
  // each row's uplink is then a lookup and not a walk of every port.
  const uplinks = useMemo(() => uplinkIndex(switches, observed, name), [switches, observed, name]);
  const by: Record<ApSortKey, (d: Device) => SortValue> = {
    name: (d) => `${labelOf(d).label}\u0000${d.serial}`,
    status: (d) => STATUS_RANK[statusOf(d).word],
    model: (d) => d.manufacturer,
    location: (d) => wordOf(locationOf(d.serial, planned, locations)),
    uplink: (d) => wordOf(apUplinkFrom(d.serial, uplinks)),
    document: (d) => { const w = runningOf(d.serial, p.convergence); return w ? DOCUMENT_RANK.get(w.word) : undefined; },
    push: (d) => pushOf(d, p.convergence)?.word,
    firmware: (d) => d.firmware,
    clients: (d) => { const ap = clients.data && firstBy(clients.data.access_points, "serial").get(d.serial); return ap && ap.state !== "unanswered" && ap.state !== "offline" ? ap.clients ?? 0 : undefined; },
    contact: contactOf,
  };
  const drawn = sortRows(rows ?? p.items, sort, by);
  const design = designOf({ kind: "venue" }, p.work);
  const same = uniformOf(drawn, [
    { key: "device", label: "Device", of: (d) => runningOf(d.serial, p.convergence) },
    // The venue's word, which is every access point's (ADR-0117): it is the same on every row by construction.
    { key: "design", label: "Design", of: () => design, to: (w) => p.work.kind === "ready" && w.tone ? WORK_LIST_PATH : undefined },
    { key: "source", label: "Source", of: (d) => { const c = apConfigurationOf(d, carried); return c.kind === "words" ? c.words : undefined; } },
    { key: "location", label: "Location", of: (d) => locationOf(d.serial, planned, locations) },
    { key: "push", label: "Last push", of: (d) => pushOf(d, p.convergence) ?? NOTHING_PUSHED },
    { key: "firmware", label: "Firmware", of: (d) => d.firmware ? { word: d.firmware } : undefined },
  ]);
  const vis = visibleOf(p.hidden, same);
  return (
    <Frame label="Access points"
      above={<SharedLine same={same} noun="access points" n={drawn.length} />}
      head={apHead(sort, onSort, p.cds && <CdsHead cds={p.cds} drawn={drawn} />, vis)}
      rows={drawn.map((d) => (
        <tr key={d.serial}>
          <Identity device={d} label={labelOf(d)} delivery={deliveries instanceof Map ? deliveries.get(d.serial) : undefined} />
          {vis("model") && <td><ModelCell device={d} carried={carried} /></td>}
          {vis("address") && <td><AddressCell device={d} addresses={p.addresses} /></td>}
          {vis("location") && <td><Words words={locationOf(d.serial, planned, locations)} /></td>}
          {vis("uplink") && <td className="word-cell"><Words words={apUplinkFrom(d.serial, uplinks)} /></td>}
          {vis("document") && <td><DocumentCell device={d} target={{ kind: "venue" }} convergence={p.convergence} work={p.work} carried={carried} same={same} /></td>}
          {vis("push") && <td><PushCell device={d} convergence={p.convergence} /></td>}
          {vis("firmware") && <td>{d.firmware ? <Clip value={d.firmware} /> : <span className="muted">—</span>}</td>}
          {vis("clients") && <td className="num"><ClientsCell device={d} clients={clients} /></td>}
          {vis("contact") && <td><When value={d.last_contact_at} /></td>}
          {p.cds && <CdsCell device={d} cds={p.cds} />}
          <Actions device={d} p={p} />
        </tr>
      ))}
      // Restates ADR-0403 decision 2b — a site whose deliveries could not be read marks no row,
      // and says so rather than letting no marker read as no upgrade under way.
      note={deliveries === "unread" && <p className="muted">Whether a firmware upgrade is under way could not be read just now, so no row is marked either way.</p>} />
  );
}

/** Each row's friendly name (#1906), with the planned device bound to its serial where the plan has one (#1907). */
function labelsOf(plan: components["schemas"]["PlannedDevice"][]): (d: Device) => EquipmentLabel {
  const bound = new Map(plan.flatMap((x) => x.serial ? [[x.serial.toLowerCase(), x] as const] : []));
  return (d) => equipmentLabel(d, bound.get(d.serial.toLowerCase()));
}

export function SwitchesTable({ observed, planned, rows, sort, onSort, ...p }: Shared & {
  observed: Map<string, Observation>;
  /** The site's plan, for the name a bound planned device gives a switch (#1907), or "unread". */
  planned?: components["schemas"]["PlannedDevice"][] | "unread";
  /** The rows to draw — `items` narrowed — in the API's order. Sorting them
   * reorders what is drawn and nothing else: the switches are read by
   * `useSwitchObservations` on `items`, the family's whole list, in the order
   * `readOrder` gives them itself — so a click on a header is never a
   * different set of controller reads (#1259). */
  rows?: Device[];
  sort?: Sort<SwitchSortKey>;
  onSort?(key: SwitchSortKey): void;
}) {
  const plan = useMemo(() => planned instanceof Array ? planned : [], [planned]);
  const name = useMemo(() => namer(p.items, plan), [p.items, plan]);
  const labelOf = useMemo(() => labelsOf(plan), [plan]);
  const isSwitch = useMemo(() => { const s = new Set(p.items.map((d) => d.serial)); return (serial: string) => s.has(serial); }, [p.items]);
  // Site-wide, so a switch placed by another switch's forwarding table says so (ADR-0448).
  const uplinks = useMemo(() => uplinksOf(p.items, observed), [p.items, observed]);
  const by: Record<SwitchSortKey, (d: Device) => SortValue> = {
    name: (d) => `${labelOf(d).label}\u0000${d.serial}`,
    status: (d) => STATUS_RANK[statusOf(d).word],
    model: (d) => d.manufacturer,
    ports: (d) => portsUpCount(observed.get(d.serial)),
    poe: (d) => poeConsumed(observed.get(d.serial)),
    uplink: (d) => wordOf(switchUplinkOf(observed.get(d.serial), isSwitch, name, uplinks.get(d.serial))),
    document: (d) => { const w = runningOf(d.serial, p.convergence); return w ? DOCUMENT_RANK.get(w.word) : undefined; },
    push: (d) => pushOf(d, p.convergence)?.word,
    firmware: (d) => d.firmware,
    contact: contactOf,
  };
  const drawn = sortRows(rows ?? p.items, sort, by);
  const same = uniformOf(drawn, [
    { key: "device", label: "Device", of: (d) => runningOf(d.serial, p.convergence) },
    // A switch's Design word is its own plan's, so it is shared only where every switch's says the same.
    { key: "design", label: "Design", of: (d) => designOf({ kind: "switch", serial: d.serial }, p.work), to: (w) => p.work.kind === "ready" && w.tone ? WORK_LIST_PATH : undefined },
    { key: "source", label: "Ports", of: (d) => switchConfigurationOf(d) },
    { key: "push", label: "Last push", of: (d) => pushOf(d, p.convergence) ?? NOTHING_PUSHED },
    { key: "firmware", label: "Firmware", of: (d) => d.firmware ? { word: d.firmware } : undefined },
  ]);
  const vis = visibleOf(p.hidden, same);
  return (
    <Frame label="Switches"
      above={<SharedLine same={same} noun="switches" n={drawn.length} />}
      head={switchHead(sort, onSort, p.cds && <CdsHead cds={p.cds} drawn={drawn} />, vis)}
      rows={drawn.map((d) => {
        const obs = observed.get(d.serial);
        return (
          <tr key={d.serial}>
            <Identity device={d} label={labelOf(d)} />
            {vis("model") && <td><ModelCell device={d} /></td>}
            {vis("address") && <td><AddressCell device={d} addresses={p.addresses} /></td>}
            {vis("ports") && <td className="word-cell"><Words words={portsOf(obs)} /></td>}
            {vis("poe") && <td className="word-cell"><Words words={poeOf(obs)} /></td>}
            {vis("uplink") && <td className="word-cell"><Words words={switchUplinkOf(obs, isSwitch, name, uplinks.get(d.serial))} /></td>}
            {vis("document") && <td><DocumentCell device={d} target={{ kind: "switch", serial: d.serial }} convergence={p.convergence} work={p.work} same={same} /></td>}
            {vis("push") && <td><PushCell device={d} convergence={p.convergence} /></td>}
            {vis("firmware") && <td>{d.firmware ? <Clip value={d.firmware} /> : <span className="muted">—</span>}</td>}
            {vis("contact") && <td><When value={d.last_contact_at} /></td>}
            {p.cds && <CdsCell device={d} cds={p.cds} />}
            <Actions device={d} p={p} />
          </tr>
        );
      })} />
  );
}

/** A family's table before its rows arrive (#1319): the same `Frame` — the
 * same name and classes, the same header carrying the page's sort, the note
 * under it — over `SKELETON_ROWS` placeholder rows, busy, with its body hidden
 * from assistive technology. The device list is the page the ask was measured
 * on: owgw's listing takes about eight seconds on production and 22 s at 2,212
 * access points — measured on 2026-10-02 in
 * `docs/evidence/2026-10-02-on-production-zenith-waits-eight-seconds-for-owgws-device-listing-and-four-in-five-whole-estate-dashboard-reads-pay-or-await-it-under-a-15-s-cache.md`
 * and
 * `docs/evidence/2026-10-02-at-2212-access-points-production-waits-22-s-for-the-listing-and-31-s-for-the-state-reads-and-serve-stale-is-warm-from-a-bound-of-one-ttl-plus-ten-seconds-whatever-the-ttl.md`
 * — and until this the page showed one sentence for that long and then drew
 * the table under it. The header is live rather than inert, on
 * purpose: a sort pressed while the placeholders stand orders the rows when
 * they arrive, which is what pressing it means. Nothing here reads anything —
 * the indexes `AccessPointsTable` builds are built once per answer, and there
 * is no answer yet. */
export function FamilySkeleton(p:
  | { family: "ap"; sort?: Sort<ApSortKey>; onSort(key: ApSortKey): void; cds?: boolean; hidden?: ReadonlySet<string> }
  | { family: "switch"; sort?: Sort<SwitchSortKey>; onSort(key: SwitchSortKey): void; cds?: boolean; hidden?: ReadonlySet<string> }) {
  // The CDS column's place is held too, for a caller who sees it, so the header does not move when the
  // rows arrive (#1319).
  const cdsHead = p.cds ? <CdsHead drawn={[]} /> : undefined;
  // The reader's hidden columns stay hidden on the skeleton; nothing is shared before the rows arrive.
  const vis = visibleOf(p.hidden, NONE_SHARED);
  const head = p.family === "switch" ? switchHead(p.sort, p.onSort, cdsHead, vis) : apHead(p.sort, p.onSort, cdsHead, vis);
  return <Frame label={p.family === "switch" ? "Switches" : "Access points"} head={head} rows={<SkeletonRows columns={headCells(head)} />} busy />;
}

// **The address a device is reached at**, on both tables, with a copy control
// (#836). It is here and not a module of its own because a module of its own
// moved rolldown's chunking: the main bundle took 537 kB against 315 kB with
// this inlined (`npm run build && npm run size`, 2026-10-14), so #201's
// ceiling refused it. It is the row's `lan_address` and nothing else, from a
// read of the list with `read_lan_address` (ADR-0391) that the page now makes
// automatically as the list loads (ADR-0443, #1901), through an async query
// that fills this column after the rows render — so the page never waits on the
// fan-out — riding the per-device read cache, so a view within the cache's TTL
// costs no controller call. It is not a display read. The SPA never derives an
// address. The
// connection's peer address (`ip_address`) is deliberately not drawn here and
// has no copy control anywhere on this page — it is not the device's address,
// and a copy control on it is how it ends up in a ticket (#836's Out).

/** Whether this browser will let the page reach the clipboard at all. Absent in
 * an insecure context and under jsdom, so it is asked rather than assumed. */
function clipboardAvailable(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.clipboard?.writeText === "function";
}

// Restates ADR-0391 and ZENITH.md §9.4: the list asks only an online row, and
// an absent address is said rather than left blank — a blank reads as "none".
// ADR-0301 keeps an offline device's last address on its own page.
const OFFLINE = "Not asked: the device is offline. Its last reported address is on its own page.";
// Restates ADR-0219 decisions 3 and 5 — a device the gateway holds no record of
// is its own state and not offline, so it is not asked and not called offline.
const UNKNOWN = "Not asked: the controller holds no record of this device.";
const NOT_ANSWERED = "No address: the device's last state report names none, the controller holds no report for it, or the read failed. Its own page reads it again.";
// Restates ADR-0443 (amending ADR-0391) — the open family's addresses are read
// automatically as the list loads; this dash is the brief gap before that read
// answers.
const UNASKED = "Not read yet: addresses load with the list.";
const UNREAD = "The address read did not answer. The list reads it again on its next load.";

/** What the list's automatic address read came to, by serial (#836, ADR-0443). */
export type AddressRead =
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "ready"; by: Map<string, string> };

/** The column's head title, kept with the cell so the two say one thing. */
// Restates ADR-0443 (amending ADR-0391) and ZENITH.md §11.2: the address is the
// device's own, from its last state report, read automatically for online
// devices, served from the read cache, and never stored.
const ADDRESS_TITLE = "The address the device holds on its own network, from its last state report — the one it is reached at. Read automatically for online devices, served from the controller's read cache, and never stored. Not the address its connection came from.";

/** One row's address: the value in code type with a Copy button, or a dash
 * that says why there is none. */
function AddressCell({ device, addresses }: { device: Device; addresses?: AddressRead }) {
  const [copied, setCopied] = useState<"yes" | "refused">();
  if (!addresses) return <span className="muted" title={UNASKED}>—</span>;
  if (addresses.kind === "loading") return <span className="muted">…</span>;
  if (addresses.kind === "error") return <span className="muted" title={UNREAD}>—</span>;
  const address = addresses.by.get(device.serial);
  if (!address) {
    const why = device.online_state === "online" ? NOT_ANSWERED : device.online_state === "offline" ? OFFLINE : UNKNOWN;
    return <span className="muted" title={why}>—</span>;
  }
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied("yes");
    } catch {
      setCopied("refused");
    }
  };
  return (
    <span className="address-cell">
      <code>{address}</code>
      {clipboardAvailable() && (
        <button type="button" className="btn btn-quiet" onClick={() => void copy()}
          aria-label={`Copy ${device.name}’s address ${address}`}>
          {copied === "yes" ? "Copied" : "Copy"}
        </button>
      )}
      {copied === "refused" && <span role="status" className="muted"> The browser refused the copy.</span>}
    </span>
  );
}
